import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import ProductLinkModal from 'components/add_product/channel-product/ProductLinkModal';
import ProductUnlinkModal from 'components/add_product/channel-product/ProductUnLinkModal';

import { channelProductApi } from 'api';

import {
  CHANNEL_PRODUCT_LIST_TAB,
  getChannelProductListColumns,
  CHANNEL_PRODUCT_FILTER_OPTIONS,
} from 'constants/product';

const SYNC_COOLDOWN_MS = 60 * 1000; // 1분

const ChannelProducts = () => {
  const tableRef = useRef();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingProduct, setIsSyncingProduct] = useState(false);
  const [syncProductCooldown, setSyncProductCooldown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const handleShowLinkModal = product => {
    setSelectedProduct(product);
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = () => {
    setShowLinkModal(false);
    setSelectedProduct(null);
  };

  const handleShowUnlinkModal = product => {
    setSelectedProduct(product);
    setShowUnlinkModal(true);
  };

  const handleCloseUnlinkModal = () => {
    setShowUnlinkModal(false);
    setSelectedProduct(null);
  };

  const handleActionSuccess = () => {
    tableRef.current?.refetch();
  };

  const handleSyncInventory = useCallback(
    async (selectedRows = [], isSyncAll = true) => {
      setIsSyncing(true);
      try {
        const payload = isSyncAll
          ? {}
          : { channel_product_ids: selectedRows.map(row => row.id) };

        const response = await channelProductApi.syncInventory(payload);

        if (isSyncAll) {
          toast.success('Successfully synced inventory');
          handleActionSuccess();
        }
      } catch (error) {
        if (isSyncAll) {
          toast.error('Failed to sync inventory');
        } else {
          throw error;
        }
      } finally {
        setIsSyncing(false);
      }
    },
    [],
  );

  const handleSyncChannelProduct = useCallback(async () => {
    setIsSyncingProduct(true);
    try {
      await channelProductApi.syncChannelProduct();
      toast.success('Successfully synced channel product');
      handleActionSuccess();
      setSyncProductCooldown(true);
      setTimeout(() => setSyncProductCooldown(false), SYNC_COOLDOWN_MS);
    } catch (error) {
      toast.error('Failed to sync channel product');
    } finally {
      setIsSyncingProduct(false);
    }
  }, []);

  const headerActions = (
    <>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSyncChannelProduct}
        disabled={isSyncingProduct || syncProductCooldown}
        className="me-2"
      >
        {isSyncingProduct ? 'Syncing...' : 'Sync Channel Product'}
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={handleSyncInventory}
        disabled={isSyncing}
      >
        {isSyncing ? 'Syncing...' : 'Sync Inventory'}
      </Button>
    </>
  );

  const bulkActions = [
    {
      value: 'sync_inventory',
      label: 'Sync Inventory',
      variant: 'primary',
      successMessage: 'Successfully synced inventory',
      errorMessage: 'Failed to sync inventory',
      handler: selectedRows => handleSyncInventory(selectedRows, false),
    },
  ];

  const extendedColumns = useMemo(() => {
    const detailColumn = {
      id: 'none',
      header: 'Action',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { product } = original;

        return (
          <Button
            variant={product ? 'danger' : 'primary'}
            size="sm"
            className="text-white"
            onClick={() =>
              product
                ? handleShowUnlinkModal(original)
                : handleShowLinkModal(original)
            }
          >
            {product ? 'UnLink' : 'Link'}
          </Button>
        );
      },
    };

    return [detailColumn, ...getChannelProductListColumns(tenant, warehouse)];
  }, [tenant, warehouse]);

  return (
    <>
      <Table
        ref={tableRef}
        title="Channel Products"
        apiService={channelProductApi}
        sortable={true}
        columns={extendedColumns}
        filterOptions={CHANNEL_PRODUCT_FILTER_OPTIONS}
        tabOptions={CHANNEL_PRODUCT_LIST_TAB}
        headerActions={headerActions}
        bulkActions={bulkActions}
      />

      <ProductLinkModal
        show={showLinkModal}
        onHide={handleCloseLinkModal}
        product={selectedProduct}
        onSuccess={handleActionSuccess}
      />

      <ProductUnlinkModal
        show={showUnlinkModal}
        onHide={handleCloseUnlinkModal}
        product={selectedProduct}
        onSuccess={handleActionSuccess}
      />
    </>
  );
};

export default ChannelProducts;
